import { isEmpty, isNumber, isNaN, isString } from 'lodash';

import { KEY_PREFIX, ShapeConstants, SYNC_KEYS, ShapeType, ROOT_SHAPE_ID, ORIGIN_SHAPE_ID, typeMap, PathCommand, StrokesAlignment } from './constant';

import type { Shape, PureFill, TextContent, TextContentParagraphSet, TextContentParagraph, TextContentText, PathContent, Point, Selrect } from './types/shape';

/**
 * 移除字符串指定前缀.
 *
 * @param content - 待移除前缀的字符串.
 * @param prefix - 前缀，默认值为"~:".
 * @returns 移除指定前缀的新字符串.
 */
export function removePrefix(content: string, prefix: string = KEY_PREFIX): string {
  return content.startsWith(prefix) ? content.replace(prefix, '') : content;
}

/**
 * fill对象转换为rgba函数字符串.
 *
 * @param fill - fill对象.
 * @returns rgba函数字符串.
 */
export function alphaHexToRgba(fill: PureFill): string {
  let i;
  // 16进制颜色值的正则
  const reg = /^#([0-9a-fA-f]{3}|[0-9a-fA-f]{6})$/;
  // 把颜色值变成小写
  let color = fill[ShapeConstants.fillColor].toLowerCase();
  if (reg.test(color)) {
    // 如果只有三位的值，需变成六位，如：#fff => #ffffff
    if (color.length === 4) {
      let colorNew = '#';
      for (i = 1; i < 4; i += 1) {
        colorNew += color.slice(i, i + 1).concat(color.slice(i, i + 1));
      }
      color = colorNew;
    }
    // 处理六位的颜色值，转为RGB
    const colorChange = [];
    for (i = 1; i < 7; i += 2) {
      colorChange.push(parseInt('0x' + color.slice(i, i + 2)));
    }
    return 'rgba(' + colorChange.join(',') + ',' + fill[ShapeConstants.fillOpacity] + ')';
  } else {
    return color;
  }
}

/**
 * rgba转换为fill对象.
 *
 * @param rgba - rgba(或rgb)函数字符串.
 * @returns fill对象.
 */
export function rgbaToAlphaHex(rgba: string): PureFill {
  const colors = rgba
    .replace(/rgba?\(/, '')
    .replace(/\)/, '')
    .replace(/[\s+]/g, '')
    .split(',');
  const r = parseInt(colors[0]).toString(16).length === 1 ? '0' + parseInt(colors[0]).toString(16) : parseInt(colors[0]).toString(16);
  const g = parseInt(colors[1]).toString(16).length === 1 ? '0' + parseInt(colors[1]).toString(16) : parseInt(colors[1]).toString(16);
  const b = parseInt(colors[2]).toString(16).length === 1 ? '0' + parseInt(colors[2]).toString(16) : parseInt(colors[2]).toString(16);

  return {
    [ShapeConstants.fillColor]: `#${r}${g}${b}`,
    [ShapeConstants.fillOpacity]: parseFloat(colors[3]) ?? 1,
  };
}

export function setStyle(shape, atom) {
  SYNC_KEYS.forEach(key => {
    const value =_cloneDeep(_get(atom, ['css', key]));

    if (isValidValue(value)) {
      if (key === ShapeConstants.content && atom.type === typeMap.get(ShapeType.Text)) {
        // 在这里处理文本内容的回显.
        const textValue =_get(atom, ['attrs', 'textValue', 'value']);

        if (isString(textValue)) {
          const firstChild =_cloneDeep(_get(value, [ShapeConstants.children, 0], {}));
          const secondChild =_cloneDeep(_get(firstChild, [ShapeConstants.children, 0], {}));
          const thirdChild =_cloneDeep(_get(secondChild, [ShapeConstants.children, 0], {}));

          // 赋值文本内容.
         _set(thirdChild, ShapeConstants.text, textValue);

          // 赋值文本数组.
         _set(secondChild, ShapeConstants.children, [thirdChild]);
         _set(firstChild, ShapeConstants.children, [secondChild]);
         _set(value, [ShapeConstants.children], [firstChild]);
        }
      }

     _set(shape, [ShapeConstants.shape, key], value);
    }
  });

  const x =_get(atom, ['css', ShapeConstants.x]);
  const y =_get(atom, ['css', ShapeConstants.y]);
  const width =_get(atom, ['css', ShapeConstants.width]);
  const height =_get(atom, ['css', ShapeConstants.height]);

  const points = [
    { [ShapeConstants.point]: { [ShapeConstants.x]: x, [ShapeConstants.y]: y } },
    { [ShapeConstants.point]: { [ShapeConstants.x]: x + width, [ShapeConstants.y]: y } },
    { [ShapeConstants.point]: { [ShapeConstants.x]: x + width, [ShapeConstants.y]: y + height } },
    { [ShapeConstants.point]: { [ShapeConstants.x]: x, [ShapeConstants.y]: y + height } },
  ];

  const selrect = {
    [ShapeConstants.rect]: {
      [ShapeConstants.x]: x,
      [ShapeConstants.y]: y,
      [ShapeConstants.width]: width,
      [ShapeConstants.height]: height,
      [ShapeConstants.x1]: x,
      [ShapeConstants.y1]: y,
      [ShapeConstants.x2]: x + width,
      [ShapeConstants.y2]: y + height,
    },
  };

 _set(shape, [ShapeConstants.shape, ShapeConstants.points], points);
 _set(shape, [ShapeConstants.shape, ShapeConstants.selrect], selrect);

  return shape;
}

export function isValidValue(value: any): boolean {
  if (isNumber(value) && !isNaN(value)) {
    return true;
  } else {
    return value !== undefined;
  }
}

export function setName(shape, atom) {
  const name: string =_get(atom, ['attrs', 'title', 'value'], '');

 _set(shape, [ShapeConstants.shape, ShapeConstants.name], name);
}

export function setId(shape, atom) {
  const prop =_get(atom, 'prop', '');
  const isRoot: boolean = prop === 'root';
  const id: string = isRoot ? ORIGIN_SHAPE_ID : prop;

 _set(shape, [ShapeConstants.shape, ShapeConstants.id], id);
}

export function setParentId(shape, atom) {
  const parent: string =_get(atom, 'parent', '');

  const isRoot: boolean = parent === '';
  const isFirstLevelChild: boolean = parent === 'root';

  const parentId: string = isRoot ? ROOT_SHAPE_ID : (isFirstLevelChild ? ORIGIN_SHAPE_ID : parent);

 _set(shape, [ShapeConstants.shape, ShapeConstants.parentId], parentId);
}

export function setFrameId(shape, atom, ast) {
  const frameId = getFrameId(atom, ast);

 _set(shape, [ShapeConstants.shape, ShapeConstants.frameId], frameId);
}

function getFrameId(atom, ast) {
  // .
  const isRoot: boolean =_get(atom, 'prop') === 'root';
  if (isRoot) {
    return ROOT_SHAPE_ID;
  }

  // .
  const isFirstLevelChild: boolean =_get(atom, 'parent') === 'root';
  if (isFirstLevelChild) {
    return ORIGIN_SHAPE_ID;
  }

  const components =_get(ast, 'components', []);
  const parent: string =_get(atom, 'parent', '');

  const parentAtom = components.find(i => i.prop === parent);
  const parentShapeType =_get(parentAtom, ['css', ShapeConstants.type]);

  if (parentShapeType === ShapeType.Frame) {
    return parentAtom.prop;
  } else {
    return getFrameId(parentAtom, ast);
  }
}

export function setShapes(shape, atom) {
  const shapeType =_get(atom, ['css', ShapeConstants.type]);

  if (![ShapeType.Frame, ShapeType.Group].includes(shapeType)) {
    return;
  }

  const children: any[] =_get(atom, 'children', []);

  const shapes = children.map(({ prop }) => prop).filter(prop => !isEmpty(prop));

 _set(shape, [ShapeConstants.shape, ShapeConstants.shapes], shapes);
}

/**
 * 根据图层类型向图层对象添加若干静态属性.
 *
 * @param shape - 待填充的图层对象.
 */
export function setOthers(shape, atom) {
  const isRootShape: boolean = shape[ShapeConstants.shape][ShapeConstants.id] === ROOT_SHAPE_ID;
  const isBoardShape: boolean =_get(atom, 'prop') === 'root';

  const isFrameType: boolean = shape[ShapeConstants.shape][ShapeConstants.type] === ShapeType.Frame;
  const isGroupType: boolean = shape[ShapeConstants.shape][ShapeConstants.type] === ShapeType.Group;
  const isTextType: boolean = shape[ShapeConstants.shape][ShapeConstants.type] === ShapeType.Text;

  // 只有Root和Board这两个图层添加.
  if (isRootShape || isBoardShape) {
    shape[ShapeConstants.shape][ShapeConstants.hideFillOnExport] = false;
  }

  // 除Root图层、组类型图层外其余的图层均添加.
  if (!isGroupType && (!isFrameType || !isRootShape)) {
    shape[ShapeConstants.shape][ShapeConstants.growType] = `${KEY_PREFIX}fixed`;
    shape[ShapeConstants.shape][ShapeConstants.hideInViewer] = false;
  }

  // 除文本类型外的图层均添加.
  if (!isTextType) {
    shape[ShapeConstants.shape][ShapeConstants.proportion] = 1;
    shape[ShapeConstants.shape][ShapeConstants.proportionLock] = false;
  }

  // 所有类型的图层均添加.
  shape[ShapeConstants.shape][ShapeConstants.transform] = { [ShapeConstants.matrix]: { [ShapeConstants.a]: 1.0, [ShapeConstants.b]: 0.0, [ShapeConstants.c]: 0.0, [ShapeConstants.d]: 1.0, [ShapeConstants.e]: 0.0, [ShapeConstants.f]: 0.0 } };
  shape[ShapeConstants.shape][ShapeConstants.transformInverse] = { [ShapeConstants.matrix]: { [ShapeConstants.a]: 1.0, [ShapeConstants.b]: 0.0, [ShapeConstants.c]: 0.0, [ShapeConstants.d]: 1.0, [ShapeConstants.e]: 0.0, [ShapeConstants.f]: 0.0 } };
}

/**
 * 获取文本图层中的所有文本内容.
 *
 * @param content - textContent对象.
 * @returns 拼接好的文本字符串.
 */
export function getShapeTexts(content: TextContent): string {
  let texts: string = '';

  const firstChildren: TextContentParagraphSet[] =_get(content, ShapeConstants.children, []);

  firstChildren.forEach(firstChild => {
    const secondChildren: TextContentParagraph[] =_get(firstChild, ShapeConstants.children, []);

    secondChildren.forEach(secondChild => {
      const thirdChildren: TextContentText[] =_get(secondChild, ShapeConstants.children, []);

      thirdChildren.forEach(thirdChild => {
        const text: string=_get(thirdChild, ShapeConstants.text, '');

        if (!['\n', '\r'].includes(text)) {
          texts += text;
        }
      });
    });
  });

  return texts;
}

/**
 * 设置pathContent的值.
 *
 * @param shape - 图层对象.
 * @param offsetX - 横坐标偏移量.
 * @param offsetY - 纵坐标偏移量.
 */
export function setPathContent(shape: Shape, offsetX: number, offsetY: number) {
  const shapeType =_get(shape, ShapeConstants.type);

  if (shapeType === ShapeType.Path) {
    const contentPath: PathContent =_get(shape, [ShapeConstants.content]);

    contentPath.forEach(pathItem => {
      const pathCommand =_get(pathItem, ShapeConstants.command);

      if (pathCommand === PathCommand.MoveTo || pathCommand === PathCommand.LineTo) {
        const paramX =_get(pathItem, [ShapeConstants.params, ShapeConstants.x], 0);
        const paramY =_get(pathItem, [ShapeConstants.params, ShapeConstants.y], 0);

       _set(pathItem, [ShapeConstants.params, ShapeConstants.x], paramX + offsetX);
       _set(pathItem, [ShapeConstants.params, ShapeConstants.y], paramY + offsetY);
      } else if (pathCommand === PathCommand.CurveTo) {
        const paramC1x =_get(pathItem, [ShapeConstants.params, ShapeConstants.c1x], 0);
        const paramC1y =_get(pathItem, [ShapeConstants.params, ShapeConstants.c1y], 0);
        const paramC2x =_get(pathItem, [ShapeConstants.params, ShapeConstants.c2x], 0);
        const paramC2y =_get(pathItem, [ShapeConstants.params, ShapeConstants.c2y], 0);
        const paramX =_get(pathItem, [ShapeConstants.params, ShapeConstants.x], 0);
        const paramY =_get(pathItem, [ShapeConstants.params, ShapeConstants.y], 0);

       _set(pathItem, [ShapeConstants.params, ShapeConstants.c1x], paramC1x + offsetX);
       _set(pathItem, [ShapeConstants.params, ShapeConstants.c1y], paramC1y + offsetY);
       _set(pathItem, [ShapeConstants.params, ShapeConstants.c2x], paramC2x + offsetX);
       _set(pathItem, [ShapeConstants.params, ShapeConstants.c2y], paramC2y + offsetY);
       _set(pathItem, [ShapeConstants.params, ShapeConstants.x], paramX + offsetX);
       _set(pathItem, [ShapeConstants.params, ShapeConstants.y], paramY + offsetY);
      }
    });
  }
}

/**
 * 生成shape对象的points数组.
 *
 * @param x - 横坐标.
 * @param y - 纵坐标.
 * @param width - 宽度.
 * @param height - 高度.
 * @returns 新的points数组.
 */
export function genPoints(x: number, y: number, width: number, height: number): Point[] {
  return [
    { [ShapeConstants.point]: { [ShapeConstants.x]: x, [ShapeConstants.y]: y } },
    { [ShapeConstants.point]: { [ShapeConstants.x]: x + width, [ShapeConstants.y]: y } },
    { [ShapeConstants.point]: { [ShapeConstants.x]: x + width, [ShapeConstants.y]: y + height } },
    { [ShapeConstants.point]: { [ShapeConstants.x]: x, [ShapeConstants.y]: y + height } },
  ];
}

/**
 * 生成shape对象的selrect对象.
 *
 * @param x - 横坐标.
 * @param y - 纵坐标.
 * @param width - 宽度.
 * @param height - 高度.
 * @returns 新的selrect对象.
 */
export function genSelrect(x: number, y: number, width: number, height: number): Selrect {
  return {
    [ShapeConstants.rect]: {
      [ShapeConstants.x]: x,
      [ShapeConstants.y]: y,
      [ShapeConstants.width]: width,
      [ShapeConstants.height]: height,
      [ShapeConstants.x1]: x,
      [ShapeConstants.y1]: y,
      [ShapeConstants.x2]: x + width,
      [ShapeConstants.y2]: y + height,
    },
  };
}

/**
 * 计算Path图层的宽高尺寸.
 *
 * @param css - css对象.
 * @param boundType - 尺寸类型，仅为宽或高.
 * @returns 计算过边框的宽高尺寸.
 */
export function calculateBound(css: any, boundType: string): number {
  const widthOrHeight =_get(css, [ShapeConstants.selrect, ShapeConstants.rect, boundType], 0);
  const strokesWidth =_get(css, [ShapeConstants.strokes, 0, ShapeConstants.strokesWidth], 0);
  const strokesAlignment =_get(css, [ShapeConstants.strokes, 0, ShapeConstants.strokesAlignment], StrokesAlignment.Center);

  let strokeWidth = 0;

  switch (strokesAlignment) {
    case StrokesAlignment.Inner:
      strokeWidth = 0;
      break;
    case StrokesAlignment.Center:
      strokeWidth = strokesWidth / 2;
      break;
    case StrokesAlignment.Outer:
      strokeWidth = strokesWidth;
      break;
  }

  return widthOrHeight + Math.ceil(strokeWidth + Math.sqrt(2 * strokeWidth * strokeWidth)) * 2;
}

/**
 * 字符串转布尔值.
 *
 * @param str - 字符串.
 * @returns 布尔值.
 */
export function stringToBoolean(str: string): boolean {
  // 处理 null、undefined 和空字符串.
  if (!str) {
    return false;
  }

  const lowerStr = str.toLowerCase();

  if (lowerStr === 'true' || lowerStr === '1') {
    return true;
  }

  if (lowerStr === 'false' || lowerStr === '0') {
    return false;
  }

  // 如果不是预期的字符串格式，返回false.
  return false;
}
